/**
 * Modèle de la formule de tarification.
 *
 * L'ancien back-office construisait la formule en manipulant le DOM : chaque opérateur
 * créait un `<div>` porteur d'une classe (`ef-addition`, `ef-var`…), et la sérialisation
 * relisait ces classes une par une. La formule n'existait donc que dans le DOM, ce qui
 * la rendait impossible à valider ou à tester hors du navigateur.
 *
 * Ici la formule est une liste de jetons ; le rendu en découle. La formule est évaluée
 * côté back par NCalc : la syntaxe sérialisée est celle de NCalc (`if(cond,a,b)`,
 * `MAX(a,b)`, comparateurs `<`, `>`, `<=`, `>=`, `=`, `!=`).
 */

export type Token =
  | { kind: 'if' }
  | { kind: 'eq' }
  | { kind: 'neq' }
  | { kind: 'lt' }
  | { kind: 'gt' }
  | { kind: 'lte' }
  | { kind: 'gte' }
  | { kind: 'add' }
  | { kind: 'sub' }
  | { kind: 'mul' }
  | { kind: 'div' }
  | { kind: 'lparen' }
  | { kind: 'rparen' }
  | { kind: 'min' }
  | { kind: 'max' }
  | { kind: 'sep' }
  | { kind: 'value'; value: string }
  | { kind: 'var'; code: string };

export type TokenKind = Token['kind'];

type FixedTokenKind = Exclude<TokenKind, 'value' | 'var'>;

/**
 * Libellé affiché sur le jeton.
 *
 * Si, Min et Max affichent leur parenthèse ouvrante : le jeton l'écrit déjà dans la
 * formule (« MAX( »), et un « Max » nu poussait à cliquer « ( » juste derrière, d'où des
 * formules « MAX((1,2) » aux parenthèses déséquilibrées.
 */
export const TOKEN_LABELS: Record<FixedTokenKind, string> = {
  if: 'Si(',
  eq: '=',
  neq: '≠',
  lt: '<',
  gt: '>',
  lte: '≤',
  gte: '≥',
  add: '+',
  sub: '−',
  mul: '×',
  div: '/',
  lparen: '(',
  rparen: ')',
  min: 'Min(',
  max: 'Max(',
  sep: ',',
};

export const SERIALIZED: Record<FixedTokenKind, string> = {
  if: 'if(',
  eq: '=',
  neq: '!=',
  lt: '<',
  gt: '>',
  lte: '<=',
  gte: '>=',
  add: '+',
  sub: '-',
  mul: '*',
  div: '/',
  lparen: '(',
  rparen: ')',
  min: 'MIN(',
  max: 'MAX(',
  sep: ',',
};

/** Jetons qui ouvrent une parenthèse, la leur ou une parenthèse libre. */
export const OPENING_KINDS: TokenKind[] = ['lparen', 'if', 'min', 'max'];

export const serializeTokens = (tokens: Token[]): string =>
  tokens
    .map((token) => {
      if (token.kind === 'value') return token.value;
      if (token.kind === 'var') return `VAR:${token.code}`;
      return SERIALIZED[token.kind];
    })
    .join('');

/** Nombre tel que NCalc le lit : point décimal, partie entière facultative (« .425 »). */
const NUMBER_PATTERN = /^(\d+\.?\d*|\.\d+)$/;

/**
 * Grammaire des jetons, source unique du tokenizer et du surlignage. Les fonctions
 * consomment leur parenthèse ouvrante ; casse et espace avant la parenthèse tolérés à la
 * frappe (« max (1,2) »). Les comparateurs à deux caractères précèdent ceux à un
 * caractère, sinon « <= » se lirait « < » puis « = ».
 */
export const TOKEN_PATTERN =
  '\\d+\\.?\\d*|\\.\\d+|VAR:[a-zA-Z0-9_]+|MIN\\s*\\(|MAX\\s*\\(|IF\\s*\\(|<=|>=|==|!=|<>|[+\\-*/();=,<>]';
const TOKEN_REGEX = new RegExp(`(${TOKEN_PATTERN})`, 'gi');

const FIXED_BY_TEXT: Record<string, FixedTokenKind> = {
  'IF(': 'if',
  '=': 'eq',
  '==': 'eq',
  '!=': 'neq',
  '<>': 'neq',
  '<': 'lt',
  '>': 'gt',
  '<=': 'lte',
  '>=': 'gte',
  '+': 'add',
  '-': 'sub',
  '*': 'mul',
  '/': 'div',
  '(': 'lparen',
  ')': 'rparen',
  ',': 'sep',
  // Séparateur de l'ancien calculateur (« MAX(a;b) ») : relu comme une virgule, que NCalc attend.
  ';': 'sep',
  'MIN(': 'min',
  'MAX(': 'max',
};

/**
 * Forme canonique, la même que le back enregistre (`ExpressionValidator.Canonicalize`) :
 * sans espaces, « ; » relu comme une virgule, fonctions en casse fixe. Sert à comparer
 * deux écritures d'une même formule.
 */
export const canonicalizeExpression = (expression: string): string =>
  expression
    .replace(/\s+/g, '')
    .replace(/;/g, ',')
    .replace(/\bMIN\(/gi, 'MIN(')
    .replace(/\bMAX\(/gi, 'MAX(')
    .replace(/\bIF\(/gi, 'if(');

export const parseExpression = (expression: string): Token[] => {
  if (!expression) return [];

  const tokens: Token[] = [];
  // `exec` en boucle plutôt que `matchAll` : la cible TypeScript du projet n'autorise
  // pas l'itération d'un IterableIterator.
  TOKEN_REGEX.lastIndex = 0;
  let match = TOKEN_REGEX.exec(expression);
  while (match !== null) {
    const raw = match[0];
    const upper = raw.toUpperCase();
    if (upper.startsWith('VAR:')) {
      tokens.push({ kind: 'var', code: raw.slice(4) });
    } else if (NUMBER_PATTERN.test(raw)) {
      tokens.push({ kind: 'value', value: raw });
    } else {
      const kind = FIXED_BY_TEXT[upper.replace(/\s+/g, '')];
      if (kind) tokens.push({ kind });
    }
    match = TOKEN_REGEX.exec(expression);
  }
  return tokens;
};

/**
 * Ce que le tokenizer ne sait pas lire dans la formule, ou null si tout est reconnu.
 *
 * Avant ce contrôle, un caractère inconnu était ignoré en silence : une formule saisie à
 * la main avec « > » ou une fonction inconnue ressortait amputée dès le premier clic sur
 * un bouton, et la version tronquée était enregistrée à la place de l'originale.
 */
export const findUnsupported = (expression: string): string | null => {
  if (!expression) return null;
  const leftover = expression.replace(TOKEN_REGEX, ' ').trim();
  if (!leftover) return null;
  return leftover.split(/\s+/).join(' ');
};

/**
 * Contrôles repris de l'ancien écran : parenthèses équilibrées, pas d'opérateurs
 * consécutifs. S'y ajoutent la détection des valeurs vides ou non numériques, qui
 * passaient silencieusement et produisaient des formules tronquées ou refusées côté back.
 */
export const validateExpression = (tokens: Token[]): string | null => {
  if (tokens.length === 0) return null;

  if (tokens.some((token) => token.kind === 'value' && token.value.trim() === '')) {
    return 'Une valeur de la formule est vide.';
  }
  if (tokens.some((token) => token.kind === 'value' && !NUMBER_PATTERN.test(token.value.trim()))) {
    return 'Une valeur de la formule n’est pas un nombre : utiliser le point comme séparateur décimal.';
  }
  if (tokens.some((token) => token.kind === 'var' && !token.code)) {
    return 'Une variable de la formule n’est pas sélectionnée.';
  }

  // Si, Min et Max portent leur propre parenthèse ouvrante.
  let depth = 0;
  for (const token of tokens) {
    if (OPENING_KINDS.includes(token.kind)) depth += 1;
    if (token.kind === 'rparen') depth -= 1;
    if (depth < 0) return 'Une parenthèse est fermée avant d’être ouverte.';
  }
  if (depth !== 0) {
    return 'Les parenthèses ne sont pas équilibrées.';
  }

  const OPERATORS: TokenKind[] = ['add', 'sub', 'mul', 'div', 'eq', 'neq', 'lt', 'gt', 'lte', 'gte'];
  for (let index = 1; index < tokens.length; index += 1) {
    if (OPERATORS.includes(tokens[index].kind) && OPERATORS.includes(tokens[index - 1].kind)) {
      return 'Deux opérateurs se suivent dans la formule.';
    }
  }

  return null;
};
